import type { AssessmentPreset, ScoreLevel } from "@/types";

/**
 * What a score means, in the framework the institution actually uses.
 *
 * Four levels, and each one described in the vocabulary a teacher already
 * marks in. A drama teacher in Bavaria works in EPA *Anforderungsbereiche*; one
 * in Sheffield works in AO1–AO4; one in Ohio works in the NCAS artistic
 * processes. Showing a bare 1–4 and hoping they map it themselves produces
 * marks that are not comparable between two teachers in the same building.
 *
 * The legend is not a translation of one scale into another. Each framework's
 * bands are its own; what they share is that there are four of them.
 */

export const SCORE_LEVELS: readonly ScoreLevel[] = [1, 2, 3, 4] as const;

/**
 * i18n keys, one per level per framework.
 *
 * Keyed rather than written here because the words are the framework's own and
 * a German teacher must read them in German — *Reproduktion* is not a
 * translation of *knowledge and understanding*, it is the same band under a
 * different system.
 */
export function legendKey(preset: AssessmentPreset, level: ScoreLevel): string {
  return `assessment.${preset}.level${level}`;
}

export function frameworkKey(preset: AssessmentPreset): string {
  return `assessment.${preset}.name`;
}

/**
 * The framework a score was recorded under, not the one in force now.
 *
 * An institution that changes framework in March must not silently restate what
 * a teacher meant in January, so a score carries its own preset and the legend
 * follows the score.
 */
export function presetForScore(
  score: { preset: AssessmentPreset } | undefined,
  institutionPreset: AssessmentPreset
): AssessmentPreset {
  return score?.preset ?? institutionPreset;
}
